import { useEffect, useCallback } from 'react'
import { weeksLived, totalWeeks } from '../lib/timeline'
import { CONFIG } from '../config/config'

const ZOOM = {
  week:   { cols: 52,  block: 7,  gap: 1 },
  month:  { cols: 48,  block: 10, gap: 1.5 },
  year:   { cols: 40,  block: 13, gap: 2 },
  decade: { cols: 20,  block: 20, gap: 3 },
}

const C = {
  bg:          '#080808',
  past:        '#131313',
  pastBorder:  '#1a1a1a',
  free:        '#0d0d0d',
  freeBorder:  '#161616',
  drift:       '#1a1800',
  driftBorder: '#252100',
}

function buildAlloc(goals, livedWeeks) {
  const map = new Map()
  let cursor = livedWeeks

  goals
    .filter(g => ['committed', 'active', 'paused'].includes(g.status))
    .sort((a, b) => (a.priority_rank ?? 999) - (b.priority_rank ?? 999))
    .forEach(g => {
      const weeks = g.estimated_weeks ?? 0
      for (let i = 0; i < weeks; i++) map.set(cursor + i, g)
      cursor += weeks
    })

  return map
}

export function useLifeCanvas(ref, { profile, goals, zoom = 'month', showDrift = false }) {
  const draw = useCallback(() => {
    const canvas = ref.current
    if (!canvas || !profile?.date_of_birth) return

    const ctx    = canvas.getContext('2d')
    const preset = ZOOM[zoom] ?? ZOOM.month
    const { cols, block, gap } = preset
    const step   = block + gap

    const total = totalWeeks(profile.expected_lifespan ?? CONFIG.DEFAULT_LIFESPAN)
    const lived = weeksLived(profile.date_of_birth)
    const rows  = Math.ceil(total / cols)
    const alloc = buildAlloc(goals ?? [], lived)

    canvas.width  = cols * step + gap
    canvas.height = rows * step + gap

    ctx.fillStyle = C.bg
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    for (let i = 0; i < total; i++) {
      const col  = i % cols
      const row  = Math.floor(i / cols)
      const x    = col * step + gap
      const y    = row * step + gap
      const past = i < lived
      const goal = alloc.get(i)

      if (goal) {
        ctx.fillStyle = (goal.color ?? C.free) + (past ? '77' : 'cc')
        ctx.fillRect(x, y, block, block)
      } else if (past && showDrift) {
        ctx.fillStyle = C.drift
        ctx.fillRect(x, y, block, block)
        ctx.strokeStyle = C.driftBorder
        ctx.lineWidth = 0.5
        ctx.strokeRect(x + 0.5, y + 0.5, block - 1, block - 1)
      } else if (past) {
        ctx.fillStyle = C.past
        ctx.fillRect(x, y, block, block)
      } else {
        ctx.fillStyle = C.free
        ctx.fillRect(x, y, block, block)
        ctx.strokeStyle = C.freeBorder
        ctx.lineWidth = 0.5
        ctx.strokeRect(x + 0.5, y + 0.5, block - 1, block - 1)
      }
    }
  }, [ref, profile, goals, zoom, showDrift])

  // Draw on mount + dep changes
  useEffect(() => {
    draw()
  }, [draw])

  // Re-draw at midnight — fix: keep timer refs in outer scope so cleanup works
  useEffect(() => {
    let timeoutId = null
    let intervalId = null

    const now          = new Date()
    const msToMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1) - now

    timeoutId = setTimeout(() => {
      draw()
      intervalId = setInterval(draw, 86400000)
    }, msToMidnight)

    // Cleanup runs correctly — both timer refs are in scope
    return () => {
      if (timeoutId  !== null) clearTimeout(timeoutId)
      if (intervalId !== null) clearInterval(intervalId)
    }
  }, [draw])
}
